import { Box, Button, ButtonProps, HStack, IconButton, IconButtonProps } from '@chakra-ui/react';
import qs from 'query-string';
import { Fragment, useEffect } from 'react';
import { NextIcon } from './next-icon';
import { PrevIcon } from './prev-icon';
import { getValidatedPageParam, paginate } from './utils';

export interface IPaginationPages {
  /**
   * pagesCount: is the total number of pages
   */
  pagesCount: number;
  /**
   * setSearchParams: is a function returns a stringified param like that "?limit=45&page=1"
   * you can use it to update your search params by passing a function and using this stringified param
   * to update your search param
   */
  setSearchParams: (params: string) => void;
  /**
   * onChange: optional function to do something when change occurs
   */
  onChange?: (currentPage: number) => void;
}

interface PageButtonProps extends ButtonProps {
  page: number;
  isActive: boolean;
}

const PageButton = ({ page, isActive, ...rest }: PageButtonProps) => (
  <Button
    variant='ghost'
    borderRadius='base'
    fontWeight='semibold'
    size='sm'
    padding='unset'
    fontSize='xs'
    bgColor={isActive ? 'red.500' : 'transparent'}
    color={isActive ? 'white' : 'black'}
    _hover={{
      bgColor: isActive ? 'red.500' : 'gray.100',
      color: isActive ? 'white' : 'black',
    }}
    _active={{
      bgColor: isActive ? 'red.500' : 'gray.200',
      color: isActive ? 'white' : 'black',
    }}
    {...rest}>
    {page}
  </Button>
);

const NextButton = (props: IconButtonProps) => (
  <IconButton variant='ghost' borderRadius='base' size='sm' icon={<NextIcon />} {...props} />
);

const PrevButton = (props: IconButtonProps) => (
  <IconButton variant='ghost' borderRadius='base' size='sm' icon={<PrevIcon />} {...props} />
);

export const PaginationPages = ({ pagesCount, setSearchParams, onChange }: IPaginationPages) => {
  const currentPage = getValidatedPageParam();
  const params = qs.parse(window.location.search);

  useEffect(() => {
    params.page = currentPage.toString();
    setSearchParams(qs.stringify(params, { skipEmptyString: true }));
  }, []);

  const handleChange = (page: number) => {
    if (page < 1 || page > pagesCount) return;

    params.page = page.toString();
    setSearchParams(qs.stringify(params, { skipEmptyString: true }));
    onChange?.(page);
  };

  return (
    <HStack spacing='xs'>
      <PrevButton aria-label='prev-btn' onClick={() => handleChange(currentPage - 1)} disabled={currentPage === 1} />
      {paginate(currentPage, pagesCount).map((page, index) => (
        <Fragment key={index}>
          {!isNaN(Number(page.toString())) && (
            <PageButton
              page={Number(page)}
              isActive={Number(page) === currentPage}
              onClick={() => handleChange(Number(page))}
            />
          )}
          {isNaN(Number(page.toString())) && (
            <Box w='md' textAlign='center' lineHeight='30px'>
              {page}
            </Box>
          )}
        </Fragment>
      ))}
      <NextButton
        aria-label='next-btn'
        onClick={() => handleChange(currentPage + 1)}
        disabled={currentPage === pagesCount}
      />
    </HStack>
  );
};
